import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'
import type { SortDirection } from 'hooks/useColumnSort'
import { PROFILE_TABLE_VARIANT_STYLES } from 'components/ds/ProfileTable/variantStyles'
import { Tooltip } from '@/components/ds2'
import { SortIcon } from './SortIcon'

interface SortableTableHeaderProps<TKey extends string> {
    label: string
    columnKey: TKey
    sortColumn?: TKey | null
    sortDirection?: SortDirection
    onSortChange?: (column: TKey) => void
    className?: string
    /** Passed through to the direction indicator — `'v2'` keeps a DS-v2 page on semantic tokens. */
    uiVariant?: 'default' | 'equipment' | 'v2'
    /** Explains what the column counts, on hover. Omitted where the label speaks for itself. */
    tooltip?: ReactNode
    /** Make the active column unmistakable: its label takes the accent color and the up/down pair becomes one
     *  arrow for its direction. For a table that is ALWAYS sorted by one of a few columns, where the half-lit
     *  pair alone left operators unsure which order they were looking at. */
    emphasizeActive?: boolean
    /** Rendered beside the sort button, not in it — for a control of its own, such as an info tooltip, that must
     *  not re-sort the column when it is clicked or tapped. */
    trailing?: ReactNode
}

/** Native buttons keep sorting available to mouse, keyboard, and assistive technology users. */
export const SortableTableHeader = <TKey extends string>({
    label,
    columnKey,
    sortColumn,
    sortDirection = 'asc',
    onSortChange,
    className,
    uiVariant,
    tooltip,
    emphasizeActive = false,
    trailing,
}: SortableTableHeaderProps<TKey>) => {
    const active = sortColumn === columnKey
    const emphasized = emphasizeActive && active
    const accent = PROFILE_TABLE_VARIANT_STYLES[uiVariant ?? 'default'].sortActive
    const Arrow = sortDirection === 'desc' ? ArrowDown : ArrowUp
    const content = onSortChange ? (
        <button
            type="button"
            onClick={() => onSortChange(columnKey)}
            className={`inline-flex w-full items-center gap-1 rounded-sm text-left cursor-pointer select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${emphasized ? accent : ''}`}
        >
            {label}
            {emphasized ? (
                <Arrow aria-hidden="true" className="h-3 w-3 shrink-0" strokeWidth={2.5} />
            ) : (
                <SortIcon direction={active ? sortDirection : undefined} active={active} uiVariant={uiVariant} />
            )}
        </button>
    ) : (
        <span>{label}</span>
    )
    return (
        <th scope="col" className={className} aria-sort={onSortChange ? (active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}>
            {trailing ? (
                <span className="inline-flex items-center gap-1">
                    {tooltip ? <Tooltip content={tooltip}>{content}</Tooltip> : content}
                    {trailing}
                </span>
            ) : tooltip ? (
                <Tooltip content={tooltip}>{content}</Tooltip>
            ) : (
                content
            )}
        </th>
    )
}
